import { useState } from "react";
import { toast } from "react-hot-toast";
import { HiOutlineChevronDown, HiOutlineClipboardCopy } from "react-icons/hi";

const CHANGE_COLUMNS = ["path", "subject", "from", "to", "fromLabel", "toLabel"];
const JSON_COLUMNS = new Set(["from", "to"]);

const cellOf = (change, column) => {
  if (JSON_COLUMNS.has(column)) return change[column] === undefined ? "undefined" : JSON.stringify(change[column]);
  return change[column] ?? "";
};

const AuditRawEvent = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const changes = event.changes || [];
  const fields = Object.fromEntries(Object.entries(event).filter(([key]) => key !== "changes" && key !== "data"));
  const data = event.data;
  const hasData = data !== undefined && data !== null;

  return (
    <div className={`rounded-lg border bg-white px-4 py-2.5 font-mono text-[12px] leading-5 transition-colors ${expanded ? "border-primary-200" : "border-gray-100"}`}>
      <div className="flex cursor-pointer items-start gap-3" onClick={() => setExpanded((current) => !current)}>
        <span className="shrink-0 tabular-nums text-gray-400">{event.createdAt}</span>
        <span className="flex min-w-0 flex-1 flex-wrap gap-x-3 gap-y-0.5">
          <span className="font-semibold text-gray-900">{event.action}</span>
          <span className="text-gray-700">{event.collectionName}</span>
          {event.documentId && <span className="text-gray-500">{event.documentId}</span>}
          <span className="text-gray-500">
            {event.actor.kind}
            {event.actor.id ? `:${event.actor.id}` : ""} <span className="text-gray-400">{event.actor.label}</span>
          </span>
          {changes.length > 0 && <span className="text-gray-400">changes[{changes.length}]</span>}
          {hasData && <span className="text-gray-400">data</span>}
          {event.route && <span className="text-gray-400">{event.route}</span>}
          {event.ip && <span className="text-gray-400">{event.ip}</span>}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(JSON.stringify(event, null, 2));
            toast.success("JSON copié");
          }}
          title="Copier le JSON de l'événement"
          className="shrink-0 rounded p-0.5 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <HiOutlineClipboardCopy className="h-4 w-4" />
        </button>
        <HiOutlineChevronDown className={`mt-0.5 h-4 w-4 shrink-0 text-gray-300 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </div>

      {expanded && (
        <div className="mt-2.5 flex flex-col gap-3 border-t border-gray-100 pt-2.5">
          <div className="grid grid-cols-[minmax(0,160px)_1fr] gap-x-4 gap-y-0.5">
            {Object.entries(fields).map(([key, value]) => (
              <div key={key} className="contents">
                <span className="text-gray-500">{key}</span>
                <span className="break-all text-gray-900">{typeof value === "object" && value !== null ? JSON.stringify(value) : String(value)}</span>
              </div>
            ))}
          </div>

          {changes.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-400">
                    {CHANGE_COLUMNS.map((column) => (
                      <th key={column} className="py-1 pr-4 font-medium">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {changes.map((change, index) => (
                    <tr key={`${change.path}-${index}`} className="border-b border-gray-50 align-top hover:bg-gray-50">
                      {CHANGE_COLUMNS.map((column) => (
                        <td
                          key={column}
                          className={`py-1 pr-4 ${column === "path" ? "whitespace-nowrap text-gray-900" : JSON_COLUMNS.has(column) ? "min-w-[160px] max-w-[360px] break-all text-gray-800" : "max-w-[240px] break-words text-gray-500"}`}
                        >
                          {cellOf(change, column)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasData && (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">data</div>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-gray-50 p-2 text-gray-800">{JSON.stringify(data, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditRawEvent;
